import { Link } from "react-router-dom";

/**
 * AlfaDrive brand lockup.
 *
 * Uses the transparent-background gold lion silhouette + a dark charcoal
 * "ALFADRIVE" wordmark with a small "DARE TO LIVE" tagline. Designed for the
 * white/charcoal fintech-clean site theme.
 *
 * Props:
 *  - variant: "lockup" (default, lion + wordmark + tagline)
 *            | "wordmark" (lion + wordmark only, no tagline)
 *            | "mark" (lion only)
 *  - height: number (px), controls the icon height and text scales with it
 *  - to: string | null, if provided wraps in Link
 *  - className: extra classes
 *  - tone: "dark" (default, dark text on light bg) | "light" (light text on dark bg)
 */
export const Logo = ({ variant = "lockup", height = 40, to = "/", className = "", tone = "dark" }) => {
  const isDark = tone === "dark";
  const wordColor = isDark ? "text-[hsl(var(--charcoal-950))]" : "text-white";
  const tagline1Color = isDark ? "text-[hsl(var(--charcoal-500))]" : "text-white/70";
  const tagline2Color = "text-[hsl(var(--danger))]";

  const mark = (
    <img
      src="/brand/alfadrive-lion-transparent.png"
      alt="AlfaDrive lion mark"
      style={{ height: `${height}px`, width: "auto" }}
      className="select-none pointer-events-none drop-shadow-[0_1px_0_rgba(0,0,0,0.04)]"
      draggable={false}
    />
  );

  let inner;
  if (variant === "mark") {
    inner = <span className={`inline-flex items-center ${className}`} aria-label="AlfaDrive" data-testid="logo-mark">{mark}</span>;
  } else {
    const wordSize = Math.max(14, Math.round(height * 0.6));
    const tagSize = Math.max(9, Math.round(height * 0.22));
    inner = (
      <span className={`inline-flex items-center gap-2.5 ${className}`} aria-label="AlfaDrive" data-testid={`logo-${variant}`}>
        {mark}
        <span className="leading-none flex flex-col gap-1">
          <span
            className={`font-display font-bold tracking-[0.05em] ${wordColor}`}
            style={{ fontSize: `${wordSize}px` }}
          >
            ALFADRIVE
          </span>
          {variant === "lockup" && (
            <span className="inline-flex items-center gap-1.5" style={{ fontSize: `${tagSize}px` }}>
              <span className={`font-medium tracking-[0.28em] uppercase ${tagline1Color}`}>Dare to</span>
              <span className={`font-semibold tracking-[0.28em] uppercase ${tagline2Color}`}>Live</span>
            </span>
          )}
        </span>
      </span>
    );
  }

  if (to === null) return inner;
  return (
    <Link to={to} className="inline-flex items-center group" data-testid="logo-link">
      <span className="transition-transform duration-200 group-hover:scale-[1.01]">{inner}</span>
    </Link>
  );
};
